import { track } from "@amplitude/analytics-browser";
import TagManager from "react-gtm-module";
import { gaID, googleAdsConversionSignUp } from "../../common/config";
import {
  handleErrorResponse,
  redirectToParticipant,
} from "../../initialAssessments/actions/assessmentActions";
import { AmplitudeEventType, AmplitudeTrack } from "../../models/amplitude";
import { PlanTypes } from "../../models/plans";
import {
  setError,
  setFetching,
} from "../../shared/request/actions/requestActions";
import {
  getQuizAnswers,
  getQuizTypes,
  login,
  registerExistingParticipant,
  registerFocusUser,
} from "../services";
import {
  filterQuestions,
  quizSettingsAdapter,
} from "../utils/quizSetingsAdapter";
import {
  DELETE_ANSWER,
  SET_ANSWER,
  SET_ANSWERS,
  SET_PROFILE,
  SET_QUIZ_ANSWERS,
  SET_QUIZ_TYPES,
} from "./actionsTypes";

export const setProfile = (participantProfile) => ({
  type: SET_PROFILE,
  payload: { participantProfile },
});

export const setAnswer = (step, answer) => ({
  type: SET_ANSWER,
  payload: { step, answer },
});

export const setUserAnswers = (step, answers) => ({
  type: SET_ANSWERS,
  payload: { step, answers },
});

export const deleteAnswer = (step, answerId) => ({
  type: DELETE_ANSWER,
  payload: { step, answerId },
});

export const setQuizTypes = (quizTypes) => ({
  type: SET_QUIZ_TYPES,
  payload: { quizTypes },
});

export const setQuizAnswers = (step, quizAnswers) => ({
  type: SET_QUIZ_ANSWERS,
  payload: { step, quizAnswers },
});

export const asyncGetQuizTypes = () => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));

  try {
    const { data: quizTypes } = await getQuizTypes();

    dispatch(setQuizTypes(filterQuestions(quizTypes.data)));
  } catch (err) {
    dispatch(
      setError({
        isOpen: true,
        message: "Something went wrong",
        type: "error",
      })
    );
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

export const asyncSetQuizAnswer = (step, quizType) => async (dispatch) => {
  dispatch(setFetching({ isFetching: true }));

  try {
    const { data: quizAnswers } = await getQuizAnswers(quizType);

    dispatch(setQuizAnswers(step, quizSettingsAdapter(quizAnswers.data[0])));
  } catch (err) {
    dispatch(
      setError({
        isOpen: true,
        message: "Something went wrong",
        type: "error",
      })
    );
  } finally {
    dispatch(setFetching({ isFetching: false }));
  }
};

export const asyncRegister =
  (details, isNewParticipant, isPasswordAssigned = false, searchParams) =>
  async (dispatch, getState) => {
    const state = getState();
    const [answer] = state.onboarding.onboarding.WANT_TO_ACHIVE.answers;

    dispatch(setFetching({ isFetching: true }));
    try {
      let data;
      if (isNewParticipant) {
        const response = await registerFocusUser(details);
        data = response.data.data;
      } else {
        const response = await registerExistingParticipant(details);
        data = response.data.data;
      }

      if (gaID) {
        const dataLayerAnalytics = {
          js: new Date(),
          event: "sign_up",
          email: details.email,
          participant_id: data.participantProfileId,
          organization_id: details.organizationId ?? null,
        };

        for (const [key, value] of searchParams?.entries() ?? []) {
          if (key.startsWith("utm_")) {
            dataLayerAnalytics[key] = value;
          }
        }

        // Google Analytics 4 custom event tracking.
        TagManager.dataLayer({ dataLayer: dataLayerAnalytics });
      }

      if (googleAdsConversionSignUp) {
        const dataLayerAds = {
          event: "conversion",
          send_to: googleAdsConversionSignUp,
          event_callback: (url?: string) => {
            if (!!url && typeof url === "string") {
              window.location.href = url;
            }
          },
        };

        // Google Ads conversion tracking.
        TagManager.dataLayer({ dataLayer: dataLayerAds });
      }

      dispatch(
        setError({
          isOpen: true,
          message: "You have completed your onboarding process",
          type: "success",
        })
      );

      if (!isPasswordAssigned) {
        const response = await login({
          userName: details.email,
          password: details.password,
        });

        if (response.status !== 200) {
          throw new Error(response.data.errors[0]);
        }

        const {
          userProfileId,
          name,
          lastName,
          userName,
          premiumSubscription,
          accessToken,
          refreshToken,
          organizationId,
          organizationName,
        } = response.data.data;

        if (isNewParticipant) {
          const amplitudeEvent: AmplitudeTrack<AmplitudeEventType.SIGN_UP> = {
            event_type: AmplitudeEventType.SIGN_UP,
            user_id: userProfileId,
            user_properties: {
              full_name: `${name} ${lastName}`,
              email: userName,
              subscription_type: premiumSubscription
                ? PlanTypes.PREMIUM
                : PlanTypes.STARTER,
              org_id: organizationId,
              org_name: organizationName,
            },
            event_properties: {
              page_url: window.location.href,
              org_code: details.promotionCode,
              selected_experience: answer?.text,
            },
          };
          await track(amplitudeEvent).promise;
        }

        const REDIRECT_TO_PARTICIPANT_DASHBOARD = `/?accessToken=${encodeURIComponent(
          accessToken
        )}&refreshToken=${encodeURIComponent(refreshToken)}`;
        redirectToParticipant(encodeURI(REDIRECT_TO_PARTICIPANT_DASHBOARD));
      } else {
        const REDIRECT_TO_PARTICIPANT_DASHBOARD = "/?participantType=1";
        redirectToParticipant(REDIRECT_TO_PARTICIPANT_DASHBOARD);
      }
    } catch (error) {
      handleErrorResponse({ error, dispatch });
      dispatch(setFetching({ isFetching: false }));
    }
  };
